// Help articles, French (Canada): AI-drafted from articles.en.js on 2026-11-01, marked for
// review (HELP_SYSTEM_PLAN.md, D3). Same ids, categories, screens and links as the
// English; on-screen names match the app's own French labels ("entrevue", "courriel").
// Example wording a candidate would say in the interview stays in English.

export const REVIEW_STATUS = "ai_draft";

export const CATEGORIES = [
  { id: "start", title: "Pour commencer" },
  { id: "setup", title: "Préparer une entrevue" },
  { id: "sound", title: "Micro et son" },
  { id: "during", title: "Pendant l’entrevue" },
  { id: "live", title: "Mode avancé" },
  { id: "report", title: "Votre rapport et vos résultats" },
  { id: "account", title: "Compte, sessions et confidentialité" },
  { id: "free", title: "Essai gratuit" },
];

export const ARTICLES = [
  // ── Pour commencer ───────────────────────────────────────────────────────
  {
    id: "how-it-works", category: "start", screens: ["upload", "account"],
    title: "Comment fonctionne le simulateur",
    body: [
      { p: "L’English Job Interview Simulator vous permet de pratiquer une véritable entrevue d’embauche en anglais et vous montre exactement comment vous améliorer." },
      { steps: [
        "Téléversez [votre CV](#upload-resume) et la [description de poste](#job-description) du poste qui vous intéresse.",
        "Choisissez votre mode de pratique : [Mode guidé ou Mode avancé](#guided-or-live).",
        "Répondez à voix haute aux 10 questions d’entrevue. L’intervieweur peut vous poser une courte [question de suivi](#follow-ups).",
        "Lisez [votre rapport](#report-overview) : votre performance en entrevue, la rétroaction sur votre anglais professionnel et une [version améliorée de chaque réponse](#polished-answer).",
      ] },
      { p: "Les questions sont créées à partir de votre CV et de la description de poste : chaque entrevue est donc différente." },
    ],
  },
  {
    id: "app-language", category: "start", screens: ["account"],
    title: "Choisir la langue de l’application",
    body: [
      { p: "La langue de l’application est celle des écrans, des instructions et de la rétroaction. Vous la choisissez à l’inscription et pouvez la modifier dans Paramètres du compte, dans le menu en haut à droite." },
      { p: "L’entrevue elle-même est toujours en anglais : les questions, vos réponses et les [réponses améliorées](#polished-answer). Les explications et les conseils sont dans la langue de l’application." },
      { p: "L’application est offerte en anglais, en portugais (Brésil), en espagnol (Amérique latine) et en français (Canada)." },
    ],
  },
  {
    id: "partner-code", category: "start", screens: ["account"],
    title: "Utiliser un code partenaire",
    body: [
      { p: "Un code partenaire vous donne accès, ou des conditions d’accès avantageuses, par l’intermédiaire de l’un de nos partenaires. Vous pouvez l’entrer à l’inscription, en créant le profil de votre compte, ou plus tard dans Paramètres du compte." },
      { steps: [
        "Ouvrez Paramètres du compte.",
        "Entrez le code dans le champ Code partenaire, exactement comme vous l’avez reçu.",
        "Sélectionnez Appliquer le code. L’écran affiche la nouvelle [date de fin de votre accès](#access-sessions).",
      ] },
      { note: "Si le code n’est pas accepté, vérifiez qu’il n’y a pas d’espaces ou de lettres mal tapées. Si cela ne fonctionne toujours pas, communiquez avec le partenaire qui vous a donné le code." },
    ],
  },
  {
    id: "sign-in", category: "start", screens: ["login"],
    title: "Se connecter et réinitialiser son mot de passe",
    body: [
      { p: "Vous pouvez vous connecter avec votre courriel et votre mot de passe, ou avec votre compte Google si vous vous êtes inscrit avec Google." },
      { steps: [
        "Pour réinitialiser votre mot de passe, sélectionnez l’onglet Récupérer le mot de passe en haut de la boîte de connexion.",
        "Entrez l’adresse courriel utilisée pour votre inscription.",
        "Ouvrez le courriel que nous vous envoyons et suivez le lien pour choisir un nouveau mot de passe.",
      ] },
      { note: "Vous n’avez pas reçu de courriel? Vérifiez votre dossier de pourriels ou de courrier indésirable, et assurez-vous d’avoir tapé la même adresse que lors de votre inscription." },
    ],
  },

  // ── Préparer une entrevue ────────────────────────────────────────────────
  {
    id: "upload-resume", category: "setup", screens: ["upload"],
    title: "Téléverser votre CV",
    body: [
      { p: "Téléversez le même CV que vous enverriez à un employeur, en format PDF ou Word (.docx)." },
      { list: [
        "Votre CV doit être en anglais. Un CV bilingue fonctionne si au moins la moitié est en anglais.",
        "Les noms d’employeurs, d’écoles et de postes dans d’autres langues ne posent pas de problème.",
        "Avant toute chose, la [vérification de confidentialité](#privacy-scan) retire vos renseignements personnels. Vous révisez ensuite le résultat.",
      ] },
      { note: "Si votre CV est refusé parce qu’il n’est pas en anglais, téléversez une version anglaise." },
    ],
  },
  {
    id: "privacy-scan", category: "setup", screens: ["upload"],
    title: "Ce que retire la vérification de confidentialité",
    body: [
      { p: "Avant que votre CV soit utilisé, la vérification de confidentialité recherche les renseignements personnels et les remplace par des étiquettes neutres comme [Name] ou [Company]." },
      { list: [
        "Coordonnées : adresses courriel, numéros de téléphone, liens vers des sites Web et des réseaux sociaux.",
        "Votre nom et celui d’autres personnes.",
        "Adresses et numéros d’identification.",
      ] },
      { p: "Vos compétences, votre expérience et vos titres de poste sont conservés, car les questions d’entrevue en ont besoin." },
      { note: "Lisez toujours le texte vérifié avant de continuer. La vérification retire tout ce qu’elle peut, mais elle n’est pas parfaite : révisez le texte et faites les modifications nécessaires. Cochez ensuite la case pour confirmer que vous avez révisé le texte et que vous en approuvez l’utilisation pour votre entrevue. Voir aussi [Vos données et votre confidentialité](#data-privacy)." },
    ],
  },
  {
    id: "job-description", category: "setup", screens: ["upload"],
    title: "Description de poste réelle ou exemple",
    body: [
      { p: "Pour une pratique plus réaliste, collez ou téléversez la vraie description du poste pour lequel vous postulez." },
      { p: "Si vous ne postulez pas à un poste précis et souhaitez simplement pratiquer vos habiletés d’entrevue, choisissez un exemple de description de poste. Vous pouvez choisir un domaine et un niveau, ou laisser l’application créer un exemple de description de poste adapté à votre CV." },
      { p: "Les questions d’entrevue combinent votre CV et la description de poste pour créer une série de questions personnalisées pour votre entrevue. Si le CV téléversé et la description de poste ont très peu en commun, vous verrez l’[avertissement de compatibilité](#fit-check)." },
    ],
  },
  {
    id: "fit-check", category: "setup", screens: ["upload"],
    title: "Ce que signifie l’avertissement de compatibilité",
    body: [
      { p: "Si votre CV et la description de poste ont très peu en commun, vous voyez un avertissement avant la création de l’entrevue. De bonnes questions de pratique ont besoin d’éléments de l’un et de l’autre." },
      { note: "L’avertissement n’est pas un jugement sur votre capacité à occuper le poste. Il signifie seulement que le simulateur pourrait avoir de la difficulté à créer des questions pertinentes pour votre entrevue." },
      { p: "Vous pouvez :" },
      { list: [
        "Téléverser une autre description de poste.",
        "Laisser l’application suggérer un poste de pratique adapté à votre CV.",
        "Pratiquer quand même.",
      ] },
      { p: "Voir l’avertissement et changer de description de poste n’a aucun effet sur le nombre de vos sessions. Pratiquer quand même utilise une [session](#what-uses-session) d’entrevue normale." },
    ],
  },
  {
    id: "guided-or-live", category: "setup", screens: ["upload"],
    title: "Mode guidé ou Mode avancé : lequel choisir",
    body: [
      { p: "Les deux modes utilisent les mêmes questions et la même évaluation. La différence, c’est la façon dont l’entrevue se déroule." },
      { list: [
        "Mode guidé : vous pouvez voir chaque question, la [réécouter](#replay-question), enregistrer quand vous êtes prêt, écouter votre réponse et la réenregistrer avant de l’envoyer. Voir [Enregistrer et terminer une réponse](#guided-recording).",
        "Mode avancé : comme une vraie entrevue. Audio seulement, sans texte. Vous avez 30 secondes pour commencer à répondre, et l’intervieweur poursuit quand vous arrêtez de parler. Voir [Comment fonctionne le Mode avancé](#live-how).",
      ] },
      { p: "Commencez par le Mode guidé si les entrevues en anglais sont nouvelles pour vous. Choisissez le Mode avancé quand vous voulez pratiquer dans des conditions réelles." },
      { note: "L’[essai gratuit](#free-trial) utilise le Mode guidé. Le Mode avancé est inclus avec l’[accès complet de 30 jours](#access-sessions)." },
    ],
  },
  {
    id: "choose-voice", category: "setup", screens: ["upload"],
    title: "Choisir la voix de l’intervieweur",
    body: [
      { p: "Choisissez un intervieweur à voix féminine ou masculine dans Paramètres de la session. Sélectionnez Écouter cette voix pour entendre d’abord un court extrait." },
      { p: "La voix lit chaque question, question de suivi et commentaire de l’entrevue. Vous pouvez régler le volume en tout temps avec la commande de volume à côté d’Aide. Voir [Aucun son ou volume trop bas](#no-sound)." },
    ],
  },

  // ── Micro et son ─────────────────────────────────────────────────────────
  {
    id: "check-sound", category: "sound", screens: ["upload", "warmup", "interview", "live"],
    title: "Vérifier mon micro et le son",
    body: [
      { steps: [
        "Si possible, utilisez des écouteurs.",
        "À l’écran de préparation, là où vous choisissez la voix féminine ou masculine de l’intervieweur, sélectionnez Écouter cette voix. Si vous n’entendez rien, voir [Aucun son ou volume trop bas](#no-sound).",
        "Utilisez la [question d’échauffement](#warm-up) avant l’entrevue pour enregistrer quelques mots et les réécouter.",
        "Si votre enregistrement est silencieux, vérifiez que le bon micro est sélectionné et [autorisé dans votre navigateur](#allow-microphone).",
      ] },
      { note: "Un endroit calme fait une grande différence, surtout en [Mode avancé](#live-how), où le silence met fin à votre réponse." },
    ],
  },
  {
    id: "allow-microphone", category: "sound", screens: ["warmup", "interview", "live"],
    title: "Autoriser le micro dans votre navigateur",
    body: [
      { p: "La première fois que vous enregistrez, votre navigateur demande la permission d’utiliser le micro. Sélectionnez Autoriser." },
      { p: "Si vous l’avez bloqué par erreur :" },
      { steps: [
        "Sélectionnez l’icône de cadenas ou de paramètres à côté de l’adresse Web.",
        "Trouvez Micro et changez le réglage pour Autoriser.",
        "Rechargez la page.",
      ] },
      { note: "Sur un téléphone ou une tablette, vérifiez aussi dans les paramètres de l’appareil que votre navigateur a le droit d’utiliser le micro. Voir aussi [Navigateurs et appareils compatibles](#browsers)." },
    ],
  },
  {
    id: "no-sound", category: "sound", screens: ["upload", "interview", "live"],
    title: "Aucun son ou volume trop bas",
    body: [
      { list: [
        "Vérifiez la commande de volume à côté d’Aide. Si elle affiche un haut-parleur barré, le son est coupé : sélectionnez-la pour le rétablir.",
        "Vérifiez le volume de votre appareil et que vos écouteurs sont branchés.",
        "Si une question ne joue pas en Mode guidé, sélectionnez Écouter la question. Si l’audio n’est pas disponible, vérifiez les paramètres de son de votre ordinateur ou de votre appareil. Voir [Réécouter une question](#replay-question).",
      ] },
    ],
  },
  {
    id: "browsers", category: "sound", screens: ["upload", "warmup", "live"],
    title: "Navigateurs et appareils compatibles",
    body: [
      { p: "Utilisez une version à jour de Chrome, Edge, Safari ou Firefox. Un ordinateur offre la meilleure expérience, mais les tablettes et les téléphones fonctionnent aussi." },
      { list: [
        "Votre navigateur doit être [autorisé à utiliser le micro](#allow-microphone).",
        "Le Mode avancé a besoin d’un navigateur capable d’enregistrer l’audio. Si le vôtre ne le peut pas, le Mode guidé vous est proposé.",
        "Fermez les autres applications qui pourraient utiliser le micro, comme les appels vidéo.",
      ] },
    ],
  },

  // ── Pendant l’entrevue ───────────────────────────────────────────────────
  {
    id: "warm-up", category: "during", screens: ["warmup"],
    title: "La question d’échauffement",
    body: [
      { p: "Avant le début de l’entrevue, vous répondez à une question d’échauffement facile. Elle vous aide à [tester votre micro](#check-sound) et à vous sentir à l’aise pour parler." },
      { p: "Enregistrez votre réponse et réécoutez-la pour vérifier le son. Rien de l’échauffement n’est envoyé pour analyse ni inclus dans votre rapport." },
      { p: "Quand vous êtes prêt, sélectionnez Continuer vers l’entrevue." },
    ],
  },
  {
    id: "guided-recording", category: "during", screens: ["interview"],
    title: "Enregistrer et terminer une réponse (Mode guidé)",
    body: [
      { steps: [
        "Écoutez la question. Vous pouvez la [réécouter](#replay-question).",
        "Sélectionnez le micro pour commencer l’enregistrement, et donnez votre réponse.",
        "Sélectionnez-le de nouveau pour arrêter. Vous pouvez écouter votre enregistrement.",
        "Sélectionnez Réenregistrer pour réessayer, ou Envoyer la réponse pour l’envoyer.",
      ] },
      { p: "Après la dernière question, sélectionnez Voir mon rapport." },
      { note: "Répondez comme vous le feriez dans une vraie entrevue : un exemple clair, ce que vous avez fait et le résultat." },
    ],
  },
  {
    id: "replay-question", category: "during", screens: ["interview"],
    title: "Réécouter une question",
    body: [
      { p: "En Mode guidé, sélectionnez Écouter la question pour l’entendre de nouveau, ou Réécouter plus lentement pour l’entendre plus lentement." },
      { p: "Si le texte de la question est masqué, sélectionnez Afficher la question pour le lire." },
      { p: "En Mode avancé, il n’y a pas de bouton pour réécouter : [demandez à l’intervieweur de répéter](#live-repeat), par exemple « Could you repeat the question, please? »" },
    ],
  },
  {
    id: "follow-ups", category: "during", screens: ["interview", "live", "report"],
    title: "Les questions de suivi",
    body: [
      { p: "Comme un vrai intervieweur, le simulateur peut poser une courte question de suivi lorsqu’il manque un élément important à votre réponse, par exemple un exemple précis ou le résultat obtenu." },
      { list: [
        "Votre réponse à la question de suivi est évaluée avec votre réponse principale.",
        "Une question de suivi n’est pas une pénalité, mais répondre de façon complète du premier coup est une excellente habitude en entrevue. Votre rapport les compte comme [Complètes dès la première réponse](#complete-first).",
      ] },
    ],
  },
  {
    id: "restart-or-end", category: "during", screens: ["warmup", "interview"],
    title: "Recommencer ou Mettre fin : ce que fait chacun",
    body: [
      { list: [
        "Recommencer reprend la même entrevue à partir de la question 1 et supprime vos réponses jusque-là. Votre CV, la description de poste et vos paramètres sont conservés. Le premier recommencement d’une entrevue est gratuit; chaque recommencement suivant utilise une [session](#what-uses-session).",
        "Mettre fin à l’entrevue arrête l’entrevue et supprime vos réponses et votre progression. La session utilisée n’est pas rétablie.",
      ] },
      { note: "Si vous avez seulement besoin d’une pause, vous n’avez pas à mettre fin à l’entrevue. Votre entrevue reste active pendant 60 minutes, tant que l’application reste ouverte dans votre navigateur." },
    ],
  },
  {
    id: "prep-failed", category: "during", screens: ["prepare"],
    title: "Si la préparation échoue",
    body: [
      { p: "La création de vos questions prend habituellement moins d’une minute. Si vous voyez « Nous n’avons pas pu préparer votre entrevue », sélectionnez Réessayer." },
      { p: "Si cela se reproduit, la cause pourrait se trouver sur nos serveurs. Attendez quelques minutes et réessayez, ou communiquez avec le soutien pour que nous puissions vérifier." },
    ],
  },

  // ── Mode avancé ──────────────────────────────────────────────────────────
  {
    id: "live-how", category: "live", screens: ["live", "upload"],
    title: "Comment fonctionne le Mode avancé",
    body: [
      { p: "Le Mode avancé ressemble davantage à une vraie entrevue : audio seulement, sans le texte des questions et sans bouton à presser entre les questions." },
      { steps: [
        "L’intervieweur pose la question.",
        "Votre micro s’active et un [compte à rebours](#live-countdown) indique combien de temps il vous reste pour commencer à répondre.",
        "Donnez votre réponse. Une fois que vous avez commencé, il n’y a pas de limite de temps.",
        "Quand vous arrêtez de parler, l’intervieweur poursuit. Voir [Terminer une réponse](#live-ending).",
      ] },
      { note: "Utilisez des écouteurs dans un endroit calme. L’aide est offerte entre les questions, pas pendant qu’une question joue ni pendant que vous répondez. Vous hésitez entre les deux modes? Voir [Mode guidé ou Mode avancé](#guided-or-live)." },
    ],
  },
  {
    id: "live-countdown", category: "live", screens: ["live"],
    title: "Le compte à rebours de 30 secondes",
    body: [
      { p: "Après chaque question, vous avez 30 secondes pour commencer à répondre (20 secondes pour une [question de suivi](#follow-ups)). Le compte à rebours s’arrête dès que vous commencez à parler." },
      { p: "Si vous ne commencez pas à temps, l’intervieweur dit « Let's move on » et passe à la question suivante. Votre rapport affiche « Aucune réponse » pour cette question, et elle compte comme sans réponse." },
      { note: "Prenez un moment pour réfléchir, puis commencez à parler. Une fois que vous avez commencé, continuez : environ 3 secondes de silence [mettent fin à votre réponse](#live-ending)." },
    ],
  },
  {
    id: "live-ending", category: "live", screens: ["live"],
    title: "Terminer une réponse et « J’ai terminé »",
    body: [
      { p: "Votre réponse se termine lorsque vous arrêtez de parler pendant environ 3 secondes, ou lorsque vous sélectionnez J’ai terminé." },
      { list: [
        "De courtes pauses pour réfléchir ne posent pas de problème : les premières secondes d’une réponse ne la terminent jamais.",
        "Sélectionnez J’ai terminé si un bruit de fond garde le micro à l’écoute après que vous avez fini.",
        "Tout ce que vous avez dit est évalué.",
      ] },
    ],
  },
  {
    id: "live-repeat", category: "live", screens: ["live"],
    title: "Demander à l’intervieweur de répéter",
    body: [
      { p: "Demandez-le simplement, comme dans une vraie entrevue (en anglais) :" },
      { list: [
        "« Could you repeat the question, please? »",
        "« Sorry, I didn't understand the question. »",
      ] },
      { p: "L’intervieweur répète la question avec les mêmes mots, et le [compte à rebours](#live-countdown) recommence. Demander une répétition ne compte pas comme une réponse." },
    ],
  },

  // ── Votre rapport et vos résultats ───────────────────────────────────────
  {
    id: "report-overview", category: "report", screens: ["report", "trial-report"],
    title: "Comprendre votre rapport",
    body: [
      { p: "Votre rapport comporte deux résultats principaux, sur 100 :" },
      { list: [
        "[Performance en entrevue](#interview-performance) : à quel point vos réponses fonctionnent comme réponses d’entrevue.",
        "[Communication en anglais professionnel](#english-dimensions) : à quel point vous communiquez bien en anglais.",
      ] },
      { p: "En dessous, chaque question affiche ses résultats, ce que vous avez dit, toute [question de suivi](#follow-ups), une [version améliorée de votre réponse](#polished-answer), ce que vous avez bien fait et ce que vous pouvez améliorer." },
      { p: "Les barres sont vertes à partir de 75, orangées de 50 à 74 et rouges sous 50." },
      { note: "Les résultats et les niveaux d’anglais sont des estimations de pratique pour guider votre préparation. Ce n’est pas une certification." },
    ],
  },
  {
    id: "interview-performance", category: "report", screens: ["report", "trial-report"],
    title: "Performance en entrevue : les six dimensions",
    body: [
      { list: [
        "Pertinence à la question : avez-vous répondu à ce qui était demandé?",
        "Preuves et exemples : avez-vous donné un exemple réel et précis?",
        "Organisation et structure : la réponse était-elle facile à suivre?",
        "Compétence professionnelle : a-t-elle montré les compétences dont le poste a besoin?",
        "Preuves de résultats : avez-vous dit quel a été le résultat? Affiché « — » lorsque la question ne demandait pas de résultat.",
        "Présence professionnelle : aviez-vous l’air sûr de vous et avez-vous assumé vos actions?",
      ] },
      { p: "Votre résultat en Performance en entrevue est la moyenne de ces dimensions. Voir aussi [Comprendre votre rapport](#report-overview)." },
    ],
  },
  {
    id: "english-dimensions", category: "report", screens: ["report", "trial-report"],
    title: "L’anglais professionnel : les cinq dimensions et les niveaux du CECR",
    body: [
      { p: "Votre anglais est mesuré selon cinq dimensions : grammaire, vocabulaire, clarté, ton professionnel et fluidité." },
      { p: "Chacune est évaluée selon des lignes directrices semblables à celles du CECR (CEFR en anglais), l’échelle internationale des niveaux de langue, puis convertie en résultat : environ 45 correspond à peu près à B1, 65 à B2 et 85 à C1." },
      { list: [
        "Solide : 80 et plus.",
        "Fonctionnel : de 60 à 79.",
        "En développement : moins de 60.",
      ] },
      { p: "Si une réponse est trop courte pour évaluer votre anglais équitablement, elle affiche « Réponse trop courte pour évaluer votre anglais » et n’est pas comptée dans votre résultat en anglais. Voir aussi [Comprendre votre rapport](#report-overview)." },
    ],
  },
  {
    id: "complete-first", category: "report", screens: ["report"],
    title: "« Complètes dès la première réponse »",
    body: [
      { p: "Ce nombre compte les questions auxquelles vous avez répondu de façon complète du premier coup, sans avoir besoin d’une [question de suivi](#follow-ups)." },
      { p: "Dans une vraie entrevue, les réponses qui incluent déjà un exemple précis et son résultat font une excellente impression. Utilisez ce nombre pour voir à quelle fréquence votre première réponse était complète." },
    ],
  },
  {
    id: "polished-answer", category: "report", screens: ["report", "trial-report"],
    title: "Utiliser la réponse améliorée et ses suggestions",
    body: [
      { p: "La réponse améliorée est votre propre réponse réécrite dans un anglais naturel et professionnel. Elle garde votre sens et n’ajoute rien que vous n’ayez pas dit." },
      { p: "Les suggestions en gras et en couleur, entre crochets, montrent ce qu’une réponse plus forte ajouterait, par exemple un résultat ou un exemple précis." },
      { note: "Remplacez chaque suggestion par des détails réels de votre expérience, puis pratiquez la réponse complète à voix haute. Vous pouvez réessayer avec [Repratiquer](#re-practice)." },
    ],
  },
  {
    id: "re-practice", category: "report", screens: ["report"],
    title: "Repratiquer",
    body: [
      { p: "Repratiquer vous permet de répondre de nouveau aux mêmes questions, une seule fois, pour appliquer ce que vous avez appris dans votre rapport." },
      { list: [
        "Utilise une [session](#what-uses-session).",
        "Offert sur la page de votre rapport juste après la fin de l’entrevue; il n’est plus disponible quand vous quittez cette page ou vous déconnectez.",
        "Votre rapport compare vos nouveaux résultats à votre première tentative.",
      ] },
      { p: "Pour pratiquer avec de nouvelles questions, préparez une nouvelle entrevue avec le même CV et la même description de poste." },
    ],
  },
  {
    id: "download-report", category: "report", screens: ["report", "downloads"],
    title: "Télécharger votre rapport",
    body: [
      { p: "Sélectionnez Télécharger le rapport sur la page de votre rapport pour l’enregistrer en PDF. Vous pouvez aussi le télécharger plus tard dans Mes rapports et téléchargements, dans le menu de votre compte." },
      { p: "Le PDF comprend vos résultats, une page de rétroaction sur votre anglais et chaque question avec votre réponse, la réponse améliorée et ce que vous pouvez améliorer." },
      { note: "Les rapports peuvent être téléchargés jusqu’à la fin de votre accès de 30 jours. Voir [Combien de temps vos rapports sont conservés](#report-retention)." },
    ],
  },

  // ── Compte, sessions et confidentialité ──────────────────────────────────
  {
    id: "access-sessions", category: "account", screens: ["account", "upload"],
    title: "Votre accès de 30 jours et vos 10 sessions",
    body: [
      { p: "L’accès complet dure 30 jours et comprend au total jusqu’à 10 sessions d’entrevue, que vous pouvez utiliser n’importe quel jour pendant la période d’accès de 30 jours." },
      { list: [
        "Sessions utilisées : affiché dans la barre du haut, par exemple « Sessions utilisées : 3 sur 10 ». Placez votre pointeur dessus pour voir quand votre accès se termine.",
        "Les sessions non utilisées prennent fin avec votre accès de 30 jours.",
        "Renouveler commence une nouvelle période de 30 jours avec 10 nouvelles sessions. L’accès ne se renouvelle pas automatiquement : pour renouveler, vous achetez une autre période de 30 jours d’accès.",
      ] },
      { p: "Voir [Ce qui utilise une session](#what-uses-session)." },
    ],
  },
  {
    id: "what-uses-session", category: "account", screens: ["account", "interview"],
    title: "Ce qui utilise une session",
    body: [
      { list: [
        "Commencer une nouvelle entrevue, en Mode guidé ou en Mode avancé : 1 session.",
        "[Repratiquer](#re-practice) : 1 session.",
        "Recommencer une entrevue : le premier recommencement est gratuit; chaque recommencement suivant utilise 1 session. Voir [Recommencer ou Mettre fin](#restart-or-end).",
        "Si une entrevue ne parvient pas à commencer, sa session est rétablie.",
        "Si la [préparation de votre entrevue personnalisée](#prep-failed) échoue, communiquez avec le soutien. Nous rétablirons votre nombre de sessions et chercherons la cause.",
      ] },
      { p: "Lire votre rapport et le télécharger n’utilisent jamais de session." },
    ],
  },
  {
    id: "report-retention", category: "account", screens: ["account", "downloads", "report"],
    title: "Combien de temps vos rapports sont conservés",
    body: [
      { p: "Votre rapport, ainsi que le CV, la description de poste et les réponses qui ont servi à le créer, sont conservés jusqu’à la fin de votre période d’accès de 30 jours. Ils sont ensuite tous supprimés automatiquement." },
      { p: "Nous vous envoyons un courriel environ 48 heures avant la suppression de vos rapports, pour que vous puissiez [télécharger le PDF](#download-report). Une fois votre accès de 30 jours terminé, vous ne pouvez plus télécharger vos rapports précédents." },
    ],
  },
  {
    id: "data-privacy", category: "account", screens: ["account", "upload"],
    title: "Vos données et votre confidentialité",
    body: [
      { list: [
        "Le CV que vous téléversez passe par une [vérification de confidentialité](#privacy-scan) avant d’être utilisé, et vous révisez le résultat. L’application n’utilise jamais votre CV original pour créer l’entrevue : seule la version que vous approuvez après la vérification est utilisée, et elle est conservée jusqu’à la fin de votre accès de 30 jours.",
        "Le texte vérifié de votre CV, la description de poste et vos réponses servent uniquement à créer et à évaluer votre entrevue de pratique.",
        "Ils sont supprimés à la fin de votre période d’accès de 30 jours (après 24 heures dans le cas d’un [essai gratuit](#free-deleted)).",
        "Des services d’IA traitent votre entrevue pour créer les questions, transcrire vos réponses et vous donner de la rétroaction.",
      ] },
      { p: "Pour tous les détails, consultez la Politique de confidentialité." },
    ],
  },

  // ── Essai gratuit ────────────────────────────────────────────────────────
  {
    id: "free-trial", category: "free", screens: ["upload", "trial-report"],
    title: "Ce que comprend l’essai gratuit",
    body: [
      { list: [
        "Une entrevue de pratique gratuite de 6 questions, en Mode guidé.",
        "Vos résultats complets à l’écran, avec une rétroaction pour chaque question.",
        "La même [vérification de confidentialité](#privacy-scan) du CV qu’avec l’accès complet.",
      ] },
      { p: "Le téléchargement du rapport, [Repratiquer](#re-practice), le recommencement et le [Mode avancé](#live-how) sont inclus avec l’[accès complet de 30 jours](#access-sessions). Vous pouvez les voir dans l’application, grisés." },
    ],
  },
  {
    id: "free-deleted", category: "free", screens: ["trial-report"],
    title: "Pourquoi vos résultats gratuits sont supprimés",
    body: [
      { p: "Les résultats de l’essai gratuit ne sont pas enregistrés. Ils sont supprimés quand vous quittez la page des résultats ou vous déconnectez, et automatiquement 24 heures après l’entrevue." },
      { p: "Lisez votre rétroaction avant de quitter la page. Avec l’accès complet, les rapports sont conservés jusqu’à la fin de votre accès de 30 jours et peuvent être [téléchargés en PDF](#download-report)." },
    ],
  },
];
